import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { useState, useEffect } from "react";
import { showNotification } from "context/notification.service";
import { partnersApi } from "@services/api/partners";
import { useProjectStore } from "@store/projectStore";

interface DecennaleRow {
  partnerId: string;
  partnerName: string;
  oldStartDate: string;
  startDate: string;
  endDate: string;
  originalStart: string;
  originalEnd: string;
  gcsPath: string;
}

function isExpired(endDate: string): boolean {
  return endDate < new Date().toISOString().slice(0, 10);
}

export const BulkEditDecennalesModal = ({
  orgId,
  onClose,
}: {
  orgId: string;
  onClose: () => void;
}) => {
  const updatePartner = useProjectStore((state) => state.updatePartner);
  const [rows, setRows] = useState<DecennaleRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "legacy" | "expired">("legacy");

  useEffect(() => {
    // Fetch all partners (not paginated) to get complete décennale data
    partnersApi.list({ org_id: orgId, limit: 5000, offset: 0 }).then((partners) => {
      const allRows: DecennaleRow[] = [];
      for (const p of partners) {
        for (const d of p.decennales || []) {
          const start = d.start_date || (d.year ? `${d.year}-01-01` : "");
          const end = d.end_date || (d.year ? `${d.year}-12-31` : "");
          allRows.push({
            partnerId: p.id,
            partnerName: p.company_name || p.display_name || "",
            oldStartDate: start,
            startDate: start,
            endDate: end,
            originalStart: start,
            originalEnd: end,
            gcsPath: d.gcs_path || "",
          });
        }
      }
      allRows.sort((a, b) => a.partnerName.localeCompare(b.partnerName));
      setRows(allRows);
      setLoading(false);
    }).catch(() => {
      showNotification({ type: "error", message: "Erreur lors du chargement" });
      setLoading(false);
    });
  }, [orgId]);

  const isLegacy = (r: DecennaleRow) =>
    r.originalStart.endsWith("-01-01") && r.originalEnd.endsWith("-12-31");

  // Latest endDate per partner — used to hide superseded expired décennales
  // from the "Expirées" tab so the list only shows partners that actually
  // still need follow-up (no replacement added yet).
  const latestEndByPartner = rows.reduce<Record<string, string>>((acc, r) => {
    if (!acc[r.partnerId] || r.endDate > acc[r.partnerId]) {
      acc[r.partnerId] = r.endDate;
    }
    return acc;
  }, {});

  const isStillExpiredForPartner = (r: DecennaleRow) =>
    isExpired(r.endDate) && r.endDate === latestEndByPartner[r.partnerId];

  const filteredRows = rows.filter((r) => {
    if (filter === "legacy") return isLegacy(r);
    if (filter === "expired") return isStillExpiredForPartner(r);
    return true;
  });

  const modifiedRows = rows.filter(
    (r) => r.startDate !== r.originalStart || r.endDate !== r.originalEnd,
  );

  const updateRow = (index: number, field: "startDate" | "endDate", value: string) => {
    setRows((prev) => {
      const next = [...prev];
      const globalIdx = prev.findIndex((r) => r === filteredRows[index]);
      if (globalIdx >= 0) {
        next[globalIdx] = { ...next[globalIdx], [field]: value };
      }
      return next;
    });
  };

  async function handleSaveAll() {
    if (modifiedRows.length === 0) return;
    setSaving(true);
    let success = 0;
    let failed = 0;
    for (const row of modifiedRows) {
      if (row.startDate >= row.endDate) {
        failed++;
        continue;
      }
      try {
        const updated = await partnersApi.updateDecennaleDates(
          row.partnerId,
          row.oldStartDate,
          row.startDate,
          row.endDate,
        );
        updatePartner(updated);
        // Update original values so the row is no longer "modified"
        row.originalStart = row.startDate;
        row.originalEnd = row.endDate;
        row.oldStartDate = row.startDate;
        success++;
      } catch {
        failed++;
      }
    }
    setSaving(false);
    setRows([...rows]); // trigger re-render
    if (failed === 0) {
      showNotification({ type: "success", message: `${success} décennale(s) mise(s) à jour` });
    } else {
      showNotification({
        type: "error",
        message: `${success} mise(s) à jour, ${failed} erreur(s)`,
      });
    }
  }

  return (
    <BaseModal
      title="Édition rapide des décennales"
      startIcon={<Icon1 name="table" size={20} />}
      onClose={onClose}
      width="max-w-4xl"
    >
      <div className="flex flex-col bg-white">
        {/* Filters */}
        <div className="flex flex-row gap-2 px-4 pt-4 pb-2">
          {(["legacy", "expired", "all"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-h3 font-medium border transition-all ${
                filter === f
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
              }`}
            >
              {f === "legacy" && `Dates approximatives (${rows.filter(isLegacy).length})`}
              {f === "expired" && `Expirées (${rows.filter(isStillExpiredForPartner).length})`}
              {f === "all" && `Toutes (${rows.length})`}
            </button>
          ))}
        </div>

        {/* Table header */}
        <div className="flex flex-row items-center px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-h4 font-semibold text-neutral-500">
          <div className="w-200 flex-shrink-0">Artisan</div>
          <div className="flex-1 px-2">Début</div>
          <div className="flex-1 px-2">Fin</div>
          <div className="w-20 text-center">Statut</div>
        </div>

        {/* Rows */}
        <div className="flex flex-col max-h-[60vh] overflow-y-auto divide-y divide-neutral-100">
          {loading && (
            <div className="text-h3 text-neutral-400 text-center py-8">
              Chargement...
            </div>
          )}
          {!loading && filteredRows.length === 0 && (
            <div className="text-h3 text-neutral-400 text-center py-8">
              Aucune décennale à afficher
            </div>
          )}
          {filteredRows.map((row, idx) => {
            const modified = row.startDate !== row.originalStart || row.endDate !== row.originalEnd;
            const expired = isExpired(row.endDate);
            return (
              <div
                key={`${row.partnerId}_${row.oldStartDate}_${idx}`}
                className={`flex flex-row items-center px-4 py-1.5 ${
                  modified ? "bg-blue-50" : expired ? "bg-red-50/30" : ""
                }`}
              >
                <div className="w-200 flex-shrink-0 text-h3 font-medium text-neutral-700 truncate pr-2" title={row.partnerName}>
                  {row.partnerName}
                </div>
                <div className="flex-1 px-2">
                  <input
                    type="date"
                    value={row.startDate}
                    onChange={(e) => updateRow(idx, "startDate", e.target.value)}
                    className={`w-full border rounded-lg px-2 py-1 text-h4 ${
                      modified ? "border-blue-300 bg-white" : "border-neutral-200"
                    }`}
                  />
                </div>
                <div className="flex-1 px-2">
                  <input
                    type="date"
                    value={row.endDate}
                    onChange={(e) => updateRow(idx, "endDate", e.target.value)}
                    className={`w-full border rounded-lg px-2 py-1 text-h4 ${
                      modified ? "border-blue-300 bg-white" : "border-neutral-200"
                    }`}
                  />
                </div>
                <div className="w-20 flex justify-center">
                  {expired ? (
                    <span className="text-h4 text-red-500 font-medium">Expirée</span>
                  ) : modified ? (
                    <span className="text-h4 text-blue-500 font-medium">Modifié</span>
                  ) : (
                    <span className="text-h4 text-green-500">Valide</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t p-4 flex flex-row justify-between items-center">
        <div className="text-h3 text-neutral-400">
          {modifiedRows.length > 0
            ? `${modifiedRows.length} modification(s) en attente`
            : "Modifiez les dates directement dans le tableau"}
        </div>
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Fermer
          </SecondaryButton>
          {modifiedRows.length > 0 && (
            <Button
              variant="small"
              label={saving ? `Sauvegarde...` : `Enregistrer (${modifiedRows.length})`}
              onClick={handleSaveAll}
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
};
